// =============================================================================
// usePayReconciliation — PAY-11 reconciliation hook
// =============================================================================
// Queries the last pay_reconciliation_runs row and provides a mutation to
// trigger a manual reconciliation run (full, per-invoice, or per-clinic).
// Only available to platform admins (RLS enforced at DB level).
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ReconciliationRunRow = {
  id:           string;
  started_at:   string;
  finished_at:  string | null;
  pass1_diffs:  Record<string, number> | null;
  pass2_diffs:  Record<string, number> | null;
  pass3_diffs:  Record<string, number> | null;
  pass4_diff:   Record<string, number> | null;
  errors_jsonb: Array<{ pass: string; error: string }>;
  trigger_type: 'cron' | 'manual';
  invoice_id:   string | null;
  clinic_id:    string | null;
};

type TriggerPayload = {
  invoice_id?: string;
  clinic_id?:  string;
};

// ---------------------------------------------------------------------------
// useLastReconciliationRun
// ---------------------------------------------------------------------------

export function useLastReconciliationRun() {
  return useQuery<ReconciliationRunRow | null>({
    queryKey: ['pay-reconciliation-last'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pay_reconciliation_runs')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ReconciliationRunRow | null;
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// useTriggerReconciliation
// ---------------------------------------------------------------------------

export function useTriggerReconciliation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TriggerPayload = {}) => {
      const { data, error } = await supabase.functions.invoke('pay-reconcile', {
        body: payload,
      });
      if (error) throw error;
      return data as { ok: boolean; data: { run_id: string } };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-reconciliation-last'] });
    },
  });
}
